"use client";

import { useForm } from "react-hook-form";

import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@stocks/ui";
import type { ProductOptionResponse } from "@stocks/shared";

import {
  useCreateProductOption,
  useUpdateProductOption,
} from "../model/useProductOption";

interface OptionValues {
  color: string;
  size: string;
}

const OptionRow = ({
  productId,
  option,
}: {
  productId: string;
  option: ProductOptionResponse;
}) => {
  const { register, handleSubmit, formState } = useForm<OptionValues>({
    values: { color: option.color, size: option.size },
  });
  const update = useUpdateProductOption(productId);

  return (
    <TableRow>
      <TableCell>
        <Input
          aria-label='색상'
          className='w-40'
          {...register("color", { required: true })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label='사이즈'
          className='w-28'
          {...register("size", { required: true })}
        />
      </TableCell>
      <TableCell>
        <Badge variant={option.isActive ? "default" : "outline"}>
          {option.isActive ? "사용 중" : "사용 중지"}
        </Badge>
      </TableCell>
      <TableCell className='flex gap-2'>
        <Button
          type='button'
          size='sm'
          disabled={!formState.isDirty || update.isPending}
          onClick={handleSubmit((v) =>
            update.mutate({
              optionId: option.id,
              values: { color: v.color.trim(), size: v.size.trim() },
            }),
          )}
        >
          저장
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          disabled={update.isPending}
          onClick={() =>
            update.mutate({
              optionId: option.id,
              values: { isActive: !option.isActive },
            })
          }
        >
          {option.isActive ? "사용 중지" : "다시 사용"}
        </Button>
      </TableCell>
    </TableRow>
  );
};

/** 제품의 색상/사이즈 옵션 목록 · 수정 · 추가 */
export const ProductOptions = ({
  productId,
  options,
}: {
  productId: string;
  options: ProductOptionResponse[];
}) => {
  const { register, handleSubmit, reset } = useForm<OptionValues>({
    defaultValues: { color: "", size: "" },
  });
  const create = useCreateProductOption(productId);

  const onAdd = async (v: OptionValues) => {
    await create.mutateAsync({ color: v.color.trim(), size: v.size.trim() });
    reset();
  };

  return (
    <div className='flex flex-col gap-4'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>색상</TableHead>
            <TableHead>사이즈</TableHead>
            <TableHead>상태</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {options.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className='text-center text-muted-foreground'>
                등록된 옵션이 없습니다.
              </TableCell>
            </TableRow>
          )}
          {options.map((o) => (
            <OptionRow key={o.id} productId={productId} option={o} />
          ))}
        </TableBody>
      </Table>
      <form
        onSubmit={handleSubmit(onAdd)}
        className='flex flex-wrap items-center gap-2'
      >
        <Input
          className='w-40'
          aria-label='새 옵션 색상'
          placeholder='색상'
          {...register("color", { required: true })}
        />
        <Input
          className='w-28'
          aria-label='새 옵션 사이즈'
          placeholder='사이즈'
          {...register("size", { required: true })}
        />
        <Button type='submit' variant='outline' disabled={create.isPending}>
          옵션 추가
        </Button>
      </form>
    </div>
  );
};
